// Typed fetch client for the flory.sh Worker. Every response is validated with a zod schema from
// packages/shared (or src/lib/types.ts where a shared schema does not exist yet), so a contract drift
// fails loudly at the boundary instead of rendering garbage.
import type { z } from 'zod';
import { ErrorBodySchema } from '@flory/shared';

export const CSRF_COOKIE = 'fl_csrf';
export const CSRF_HEADER = 'X-FL-CSRF';

export class ApiError extends Error {
  readonly status: number;
  readonly code: string;
  readonly requestId: string | null;
  readonly details: unknown;

  constructor(
    status: number,
    code: string,
    message: string,
    requestId: string | null = null,
    details?: unknown,
  ) {
    super(message);
    this.name = 'ApiError';
    this.status = status;
    this.code = code;
    this.requestId = requestId;
    this.details = details;
  }
}

export function readCookie(name: string): string | null {
  for (const part of document.cookie.split(';')) {
    const idx = part.indexOf('=');
    if (idx < 0) continue;
    if (part.slice(0, idx).trim() === name) return decodeURIComponent(part.slice(idx + 1).trim());
  }
  return null;
}

const SAFE = new Set(['GET', 'HEAD', 'OPTIONS']);

interface RequestOptions<S extends z.ZodType | undefined> {
  method: string;
  body?: unknown;
  /** Raw bytes (uploads): sent as-is with their own content type instead of JSON. */
  raw?: Blob;
  schema?: S;
  signal?: AbortSignal;
}

async function request<S extends z.ZodType | undefined>(
  path: string,
  opts: RequestOptions<S>,
): Promise<S extends z.ZodType ? z.infer<S> : undefined> {
  const method = opts.method.toUpperCase();
  const headers = new Headers({ accept: 'application/json' });
  if (!SAFE.has(method)) {
    const csrf = readCookie(CSRF_COOKIE);
    if (!csrf) {
      throw new ApiError(
        401,
        'csrf_missing',
        'Your session cookie is missing or expired. Sign in again to make changes.',
      );
    }
    headers.set(CSRF_HEADER, csrf);
  }
  let body: string | Blob | undefined;
  if (opts.raw) {
    if (!opts.raw.type)
      throw new ApiError(0, 'upload_type_missing', `Cannot upload to ${path}: the file has no content type.`);
    headers.set('content-type', opts.raw.type);
    body = opts.raw;
  } else if (opts.body !== undefined) {
    headers.set('content-type', 'application/json');
    body = JSON.stringify(opts.body);
  }

  let res: Response;
  try {
    res = await fetch(path, {
      method,
      headers,
      body,
      credentials: 'same-origin',
      signal: opts.signal,
    });
  } catch (e) {
    if (e instanceof DOMException && e.name === 'AbortError') throw e;
    throw new ApiError(
      0,
      'network_error',
      `Could not reach the server for ${method} ${path}: ${e instanceof Error ? e.message : String(e)}. Check your connection and retry.`,
    );
  }
  const requestId = res.headers.get('x-request-id');

  if (!res.ok) {
    const text = await res.text();
    let parsed: unknown = null;
    try {
      parsed = text ? JSON.parse(text) : null;
    } catch {
      parsed = null; // Non-JSON error body: reported verbatim below.
    }
    const env = ErrorBodySchema.safeParse(parsed);
    if (env.success) {
      throw new ApiError(
        res.status,
        env.data.error.code,
        env.data.error.message,
        requestId,
        env.data.error.details,
      );
    }
    throw new ApiError(
      res.status,
      `http_${res.status}`,
      `${method} ${path} failed with HTTP ${res.status}${text ? `: ${text.slice(0, 200)}` : ''}`,
      requestId,
    );
  }

  if (res.status === 204 || !opts.schema) {
    return undefined as S extends z.ZodType ? z.infer<S> : undefined;
  }
  const json: unknown = await res.json();
  const out = opts.schema.safeParse(json);
  if (!out.success) {
    const issues = out.error.issues
      .slice(0, 3)
      .map((i) => `${i.path.join('.') || '(root)'}: ${i.message}`)
      .join('; ');
    throw new ApiError(
      res.status,
      'response_shape_mismatch',
      `Unexpected response from ${method} ${path} (${issues}). The API contract changed; update packages/shared or src/lib/types.ts.`,
      requestId,
      out.error.issues,
    );
  }
  return out.data as S extends z.ZodType ? z.infer<S> : undefined;
}

export const api = {
  get: <S extends z.ZodType>(path: string, schema: S, signal?: AbortSignal) =>
    request(path, { method: 'GET', schema, signal }),
  post: <S extends z.ZodType | undefined = undefined>(path: string, body?: unknown, schema?: S) =>
    request(path, { method: 'POST', body, schema }),
  patch: <S extends z.ZodType | undefined = undefined>(path: string, body?: unknown, schema?: S) =>
    request(path, { method: 'PATCH', body, schema }),
  put: <S extends z.ZodType | undefined = undefined>(path: string, body?: unknown, schema?: S) =>
    request(path, { method: 'PUT', body, schema }),
  del: <S extends z.ZodType | undefined = undefined>(path: string, schema?: S, body?: unknown) =>
    request(path, { method: 'DELETE', schema, body }),
  // `method` defaults to POST (avatar/resume uploads); the org logo route is a PUT (A07).
  upload: <S extends z.ZodType | undefined = undefined>(
    path: string,
    file: Blob,
    schema?: S,
    opts?: { method?: 'POST' | 'PUT' },
  ) => request(path, { method: opts?.method ?? 'POST', raw: file, schema }),
};

/** Human-readable error for UI surfaces; includes request id for support/debugging. */
export function describeError(e: unknown): string {
  if (e instanceof ApiError)
    return e.requestId ? `${e.message} (request ${e.requestId})` : e.message;
  if (e instanceof Error) return e.message;
  return String(e);
}
